import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Divider,
  Typography,
  Switch,
  Box,
  Paper,
  Stack,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Container,
  FormControlLabel,
  TextField,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const API_URL = "https://api.a2ajobs.com/api";
const GET_MEMBERS_API = `${API_URL}/setting-team-member`;
const ADD_MEMBER_API = `${API_URL}/add-setting-team-member`;
const DELETE_MEMBER_API = `${API_URL}/delete-setting-team-member`;
const UPDATE_SETTING_API = `${API_URL}/update-setting`; // Add this API endpoint

const SettingPage = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [allowRating, setAllowRating] = useState(false);
  const [email, setEmail] = useState("");

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/setting`);
      if (response.data?.data?.allow_rating_per_question) {
        setAllowRating(response.data.data.allow_rating_per_question === "1");
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(GET_MEMBERS_API);
      setTeamMembers(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  };

  const handleAddMember = async () => {
    if (!email) {
      alert("Please enter a valid email address.");
      return;
    }
    try {
      await axios.post(ADD_MEMBER_API, { email });
      setEmail(""); // Clear input after success
      fetchTeamMembers(); // Refresh team members list
    } catch (error) {
      console.error("Error adding member:", error);
    }
  };

  const handleDeleteMember = async (id) => {
    try {
      await axios.delete(`${DELETE_MEMBER_API}/${id}`);
      fetchTeamMembers(); // Refresh list after deletion
    } catch (error) {
      console.error("Error deleting member:", error);
    }
  };

  // Add this function to handle the switch change
  const handleAllowRatingChange = async (event) => {
    const newValue = event.target.checked;
    setAllowRating(newValue);

    try {
      // Update the setting on the server
      await axios.post(UPDATE_SETTING_API, {
        allow_rating_per_question: newValue ? "1" : "0"
      });
    } catch (error) {
      console.error("Error updating setting:", error);
      // Revert the state if the API call fails
      setAllowRating(!newValue);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchTeamMembers();
  }, []);

  return (
    <Container maxWidth="lg">
      <Paper sx={{ p: 3, my: 4 }}>
        <Box sx={{ p: { md: 3, xs: 0 } }}>
          <Stack>
            <Typography variant="h6" fontWeight={600}>
              Rating Settings
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Manage rating related settings
            </Typography>
          </Stack>
          <Divider />
          <Stack my={3}>
            <Typography variant="h6">Allow rating per question</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={allowRating}
                  onChange={handleAllowRatingChange} // Add this onChange handler
                />
              }
              label="Enabled"
            />
          </Stack>
          <Divider />
          <Typography variant="h6" sx={{ mt: 3 }} fontWeight={600}>
            Interview Finished Notifications
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Set which interview completion notifications will be set by default when creating a new position.
          </Typography>
          <Box display="flex" alignItems="center" sx={{ mt: 1 }}>
            <FormControlLabel control={<Switch defaultChecked />} label="Inviter receives notifications" />
          </Box>

          {/* Invite Team Members */}
          <Typography variant="h6" sx={{ mt: 3 }} fontWeight={600}>
            Add Team Member
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }} >
            Enter the email address of the person you want to invite.

          </Typography>


          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <TextField
              label="Enter Email"
              variant="outlined"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
            />
            <Button variant="contained" onClick={handleAddMember} sx={{ borderRadius: 10, px: 5, py: 0 }}>
              Invite
            </Button>
          </Box>

          <Typography mt={3} fontWeight={600}>
            Who will receive notifications
          </Typography>
          <List>
            {teamMembers.map((member, index) => (
              <ListItem key={index} divider>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: "#1976d2" }}>{member.name?.charAt(0)}</Avatar>
                </ListItemAvatar>
                <ListItemText primary={member.name} secondary={member.email} />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => handleDeleteMember(member.id)}>
                    <CloseIcon color="error" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      </Paper>
    </Container>
  );
};

export default SettingPage;